import { Cars } from "data/types";
import BtnLikeIcon from "components/BtnLikeIcon/BtnLikeIcon";
import SaleOffBadge from "components/SaleOffBadge/SaleOffBadge";
import GallerySlider from "components/GallerySlider/GallerySlider";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { converToNumber, createUrlString } from "utils/utils";
import { Link } from "react-router-dom";

export interface CarCardProps {
    className?: string;
    data: Cars;
    size?: "default" | "small";
    isLiked?: boolean;
}

const CarCardList: React.FC<CarCardProps> = ({ className = "", data, isLiked = false }) => {
    const {
        color,
        fuelType,
        meta: { carId, advertiserId },
        media,
        make,
        mileage,
        model,
        priceGBP,
        transmissionType,
        yearOfManufacture,
        body,
        doors = 4,
        plate,
        seats,
        monthlyPayment,
        dateUploaded,
        derivative,
        attentionGrabber
    } = data;


    const renderSliderGallery = () => {
        // only show 5 images
        const images = media.images.slice(0, 5).map((item, index) => {
            return item.href
        })

        return (
            <div className="pt-3 relative w-full flex items-center justify-center md:w-72 flex-shrink-0 border-r border-slate-100 dark:border-zinc-800 ">
                <div className="absolute top-6 left-3 z-20 lg:hidden">
                    {new Date(dateUploaded).getTime() > new Date().getTime() - (7 * 24 * 60 * 60 * 1000) ? (
                        <SaleOffBadge desc="New in stock" />
                    ) : null}
                </div>
                <div className="absolute top-3 right-3 z-20 lg:right-0 lg:left-3">
                    <BtnLikeIcon
                        isLiked={isLiked}
                        carId={carId}
                    />
                </div>
                <div className="w-full min-h-[210px] h-full">
                    <GallerySlider
                        href={`/car/${createUrlString(make, model, carId)}`}
                        uniqueID={`CarCard_${carId}`}
                        galleryImgs={images}
                        imgClassname="w-full h-[210px]"
                    />
                </div>
            </div>
        );
    };

    const renderContent = () => {
        return (
            <div className="flex-grow px-5 py-4 flex flex-col dark:bg-custom-900 hover:dark:bg-custom-800 transition-all">
                <div className="space-y-2">
                    <div className="flex flex-col lg:flex-row justify-between lg:space-x-2">
                        <Link to={`/car/${createUrlString(make, model, carId)}`}>
                            <h2 className="text-xl font-semibold capitalize">
                                <span className="line-clamp-1 uppercase">{make} {model}</span>
                            </h2>
                        </Link>

                        <span className="text-base font-display font-semibold">
                            £{priceGBP}
                            {` `}
                            {monthlyPayment && (
                                <span className="text-xs text-neutral-500 dark:text-neutral-400 font-normal">
                                    from £{monthlyPayment}/pm
                                </span>
                            )}
                        </span>
                    </div>
                    <div className="flex flex-col text-sm text-neutral-500 dark:text-neutral-400">
                        <div className="flex flex-col">
                            <span className="text-xs font-body block text-white font-normal">{derivative}</span>
                            <span className="text-xs font-body block text-gray-500 my-1">{attentionGrabber}</span>
                        </div>

                        <div className="flex items-center">
                            <span className="hidden sm:inline-block  text-base">
                                <i className="las la-map-marked"></i>
                            </span>
                            <span className="sm:ml-2 line-clamp-1">{advertiserId && advertiserId === "10035437" ? "Cotswolds" : "Redline HQ"}</span>
                        </div>
                    </div>
                </div>

                {/* SHOW MOBILE */}
                <div className="block md:hidden lg:hidden my-3 lg:my-1">
                    <div className={`grid grid-cols-3 text-neutral-500 dark:text-neutral-400 text-sm gap-2`}>
                        <span className="flex items-center sm:mr-1">
                            <i className="fa-sharp fa-light fa-tachometer-alt mr-1"></i>
                            {converToNumber(mileage!)}
                            <span className="hidden lg:block ml-1">{parseInt(mileage!) > 9000 ? "Mi" : "Miles"}</span>
                            <span className="block lg:hidden ml-1">Mi</span>
                        </span>
                        <span className="flex items-center sm:mr-2">
                            <CalendarDaysIcon className="w-4 h-4 mr-1" />
                            {yearOfManufacture}  ({plate})
                        </span>
                        <span className="flex items-center sm:mr-2">
                            <i className="fal fa-cog mr-1"></i>
                            {transmissionType}
                        </span>
                        {body && (
                            <span className="flex items-center sm:mr-2">
                                <i className="fa-light fa-car-wrench mr-1"></i>
                                {body}
                            </span>
                        )}
                        {color && (
                            <span className="flex items-center sm:mr-2">
                                <i className="fa-light fa-spray-can w-4 h-4 mr-1"></i>
                                {color}
                            </span>
                        )}
                        {fuelType && (
                            <span className="flex items-center sm:mr-2">
                                <i className="fa-light fa-gas-pump mr-1"></i>
                                {fuelType}
                            </span>
                        )}
                    </div>
                </div>

                {/* SHOW DESK */}
                <div className="hidden lg:grid grid-cols-3 items-center gap-2 gap-y-3 mt-4">
                    {seats && (
                        <div className="flex items-center space-x-2">
                            <i className="fal fa-person-seat"></i>
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                                {seats} seats
                            </span>
                        </div>
                    )}
                    {doors && (
                        <div className="flex items-center space-x-2">
                            <i className="fal fa-door-open "></i>
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                                {doors} doors
                            </span>
                        </div>
                    )}

                    {body && (
                        <div className="flex items-center space-x-2">
                            <i className="fa-light fa-car-wrench"></i>
                            <span className="text-sm text-neutral-500 dark:text-neutral-400">
                                {body}
                            </span>
                        </div>
                    )}

                    <div className="flex items-center space-x-2">
                        <i className="fa-sharp fa-light fa-tachometer-alt"></i>
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">
                            {converToNumber(mileage!)} Miles
                        </span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <i className="fa-light fa-gas-pump"></i>
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">
                            {fuelType}
                        </span>
                    </div>
                    <div className="flex items-center space-x-2">
                        <i className="fal fa-cog"></i>
                        <span className="text-sm text-neutral-500 dark:text-neutral-400">
                            {transmissionType}
                        </span>
                    </div>
                </div>

                <div className="hidden justify-between items-start lg:flex absolute bottom-2">
                    {new Date(dateUploaded).getTime() > new Date().getTime() - (7 * 24 * 60 * 60 * 1000) ? (
                        <SaleOffBadge desc="New in stock" />
                    ) : null}
                </div>

                <Link to={`/car/${createUrlString(make, model, carId)}`} className="flex flex-col w-full my-3 lg:my-0 lg:items-end">
                    <span className="flex items-center text-red-500 text-sm font-medium transition-colors">
                        View Now
                        <span className="bg-red-600 text-white dark:text-black w-5 h-5 rounded-full flex items-center justify-center ml-1 p-3">
                            <i className="fa-solid fa-chevron-right" />
                        </span>
                    </span>
                </Link>
            </div>
        );
    };

    return (
        <div
            className={`nc-CarCardH group relative bg-white dark:bg-custom-900   rounded-2xl overflow-hidden border-none ${className}`}
            data-nc-id="CarCardH"
        >
            <div className="flex flex-col md:flex-row lg:flex-row">
                <div>
                    {renderSliderGallery()}
                </div>
                {renderContent()}
            </div>
        </div>
    );
};

export default CarCardList;
